// Copyright (c) Ping. All rights reserved.
//
// "Who's going?" sheet — opened from a meeting card's counts line. Lists every
// RSVP grouped by response (Going / Maybe / Not going) with the member's avatar
// + name. Pure presentation: the host resolves voter identities from its live
// `MeetingState.rsvps` + the group roster and passes them in.

import React from 'react';
import { ScrollView, Text, View } from 'react-native';

import { Check, User, X } from 'lucide-react-native';

import { Avatar, Modal } from '@ping/ui';

export interface MeetingResponder {
  readonly accountId: string;
  readonly name: string;
  readonly avatarUri?: string | undefined;
}

export interface MeetingResponses {
  readonly yes: ReadonlyArray<MeetingResponder>;
  readonly maybe: ReadonlyArray<MeetingResponder>;
  readonly no: ReadonlyArray<MeetingResponder>;
}

interface Props {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly responses: MeetingResponses;
}

const SECTIONS: ReadonlyArray<{
  readonly key: keyof MeetingResponses;
  readonly label: string;
  readonly icon: React.ReactNode;
}> = [
  { key: 'yes', label: 'Going', icon: <Check size={14} strokeWidth={2.5} color="#3BA55D" /> },
  { key: 'maybe', label: 'Maybe', icon: <User size={14} strokeWidth={2} color="#E0A63B" /> },
  { key: 'no', label: 'Not going', icon: <X size={14} strokeWidth={2.5} color="#E5484D" /> },
];

export function MeetingResponsesModal({ visible, onClose, title, responses }: Props): React.ReactElement {
  const total = responses.yes.length + responses.maybe.length + responses.no.length;
  return (
    <Modal visible={visible} onClose={onClose} accessibilityLabel="Meeting responses">
      <View className="w-[400px] max-w-full">
        <View className="flex-row items-center justify-between">
          <Text className="font-sans text-[18px] font-bold text-light-text-primary dark:text-white" numberOfLines={1}>
            Responses
          </Text>
          <Text className="font-sans text-[13px] text-light-text-secondary dark:text-[#8C929E]">{total}</Text>
        </View>
        <Text className="mt-1 font-sans text-[13px] text-light-text-secondary dark:text-[#8C929E]" numberOfLines={1}>
          {title}
        </Text>

        <ScrollView className="mt-4 max-h-[60vh]" showsVerticalScrollIndicator={false}>
          {SECTIONS.map((section) => {
            const people = responses[section.key];
            return (
              <View key={section.key} className="mb-4">
                <View className="flex-row items-center gap-1.5">
                  {section.icon}
                  <Text className="font-sans text-[12px] font-semibold uppercase tracking-wide text-light-text-secondary dark:text-[#8C929E]">
                    {section.label} · {people.length}
                  </Text>
                </View>
                {people.length === 0 ? (
                  <Text className="mt-2 font-sans text-[13px] text-light-text-tertiary dark:text-[#6B7280]">
                    No one yet
                  </Text>
                ) : (
                  people.map((p) => (
                    <View key={p.accountId} className="mt-2 flex-row items-center gap-2.5">
                      <Avatar
                        size="small"
                        shape="rounded"
                        initials={(p.name.charAt(0) || '?').toUpperCase()}
                        {...(p.avatarUri !== undefined ? { imageUri: p.avatarUri } : {})}
                      />
                      <Text className="font-sans text-[14px] text-light-text-primary dark:text-[#F5F6F8]" numberOfLines={1}>
                        {p.name}
                      </Text>
                    </View>
                  ))
                )}
              </View>
            );
          })}
        </ScrollView>
      </View>
    </Modal>
  );
}
